import { Stack, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GoalAmountSection } from "web_myplann/Goals/Components/AmountSections/GoalAmountSection";
import { CollapseFadeTransition } from "web_myplann/Goals/Components/CollapseFadeTransition";
import { GoalCategorySelector } from "web_myplann/Goals/Components/GoalCategorySelector";
import { MonthlySavingsConversion } from "web_myplann/Goals/Components/MonthlySavingsConversion";
import { RealizationBox } from "web_myplann/Goals/Components/RealizationBox";
import { useGoalForm } from "web_myplann/Goals/Forms/useGoalForm";
import {
	monthlyContribution,
	monthsUntil,
} from "web_myplann/Goals/Helpers/monthlyContribution";
import { useGoalCategoryStrings } from "web_myplann/Goals/Hooks/useGoalCategoryStrings";
import { GoalCategoryTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getRoute } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { CollapsibleSection } from "web_myplann/Shared/Components/CollapsibleSection";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";

const parseCategoryParam = (
	raw: string | null,
): GoalCategoryTypeEnum | undefined => {
	if (!raw) return undefined;
	return (Object.values(GoalCategoryTypeEnum) as string[]).includes(raw)
		? (raw as GoalCategoryTypeEnum)
		: undefined;
};

export const GoalFormPage = () => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();

	const [searchParams] = useSearchParams();
	const initialCategory = parseCategoryParam(searchParams.get("category"));

	const goToList = () => {
		navigate(getRoute(Routing.Goals.GoalsList));
	};

	const handleSuccess = (goalId?: number) => {
		if (goalId == null) {
			goToList();
			return;
		}
		navigate(Routing.Goals.GoalDetail.path(String(goalId)), {
			state: { welcomePin: true, ownFunds },
		});
	};

	const { control, submit, isSubmitting, watch, setValue, getValues } =
		useGoalForm({
			initialCategory,
			onSuccess: handleSuccess,
		});

	const minDueDate = useMemo(() => new Date(), []);
	const category = watch("category");
	const totalValue = watch("totalValue") ?? 0;
	const ownFunds = watch("ownFunds") ?? 0;
	const loanAmount = watch("loanAmount") ?? 0;
	const dueDate = watch("dueDate");
	const savingsTarget = Math.max(0, totalValue - ownFunds - loanAmount);
	const realizationMode = totalValue > 0 && savingsTarget === 0;
	const hasMonthlyHero =
		!realizationMode &&
		savingsTarget > 0 &&
		monthsUntil(dueDate) > 0 &&
		monthlyContribution(savingsTarget, dueDate) > 0;
	const submitDisabled = !totalValue || totalValue <= 0 || isSubmitting;

	const { defaultName, sectionTitle } = useGoalCategoryStrings(category);

	const lastSuggestedNameRef = useRef<string>("");

	useEffect(() => {
		if (!category) return;
		const currentName = getValues("name") ?? "";
		if (currentName === "" || currentName === lastSuggestedNameRef.current) {
			setValue("name", defaultName, {
				shouldDirty: false,
				shouldValidate: false,
			});
		}
		lastSuggestedNameRef.current = defaultName;
	}, [category, defaultName, getValues, setValue]);

	return (
		<MyPlannLayout
			bgColor={theme.palette.grey[50]}
			disableBottomBarSpacing={realizationMode || hasMonthlyHero}
			disableContentPadding
			topBar={
				<NavigationHeader
					defaultRoute={Routing.Goals.GoalsList}
					title={t(Resources.Goals.Form.titleCreate)}
					bgColor={theme.palette.background.paper}
				/>
			}
		>
			<form onSubmit={submit}>
				<Stack>
					<CollapsibleSection title={t(Resources.Goals.Form.sectionCategory)}>
						<GoalCategorySelector control={control} name="category" />
					</CollapsibleSection>

					<CollapseFadeTransition
						show={Boolean(category)}
						transitionKey={category ?? ""}
					>
						<CollapsibleSection title={t(Resources.Goals.Form.sectionAbout)}>
							<Stack bgcolor={theme.palette.background.paper}>
								<FlatRowFormInput
									control={control}
									name="name"
									label={t(Resources.Goals.Form.nameLabel)}
									placeholder={t(Resources.Goals.Form.namePlaceholder)}
								/>
								<FlatRowFormDatePicker
									control={control}
									name="dueDate"
									label={t(Resources.Goals.Form.dueDateLabel)}
									minDate={minDueDate}
									isLast
								/>
							</Stack>
						</CollapsibleSection>

						<CollapsibleSection title={sectionTitle}>
							<GoalAmountSection control={control} category={category} />
						</CollapsibleSection>

						{realizationMode ? (
							<RealizationBox
								category={category}
								submitLabel={t(Resources.Goals.Form.submitCreate)}
								cancelLabel={t(Resources.Goals.Form.cancel)}
								submitDisabled={submitDisabled}
								isSubmitting={isSubmitting}
								onCancel={goToList}
							/>
						) : (
							<MonthlySavingsConversion
								savingsTarget={savingsTarget}
								dueDate={dueDate}
								submitLabel={t(Resources.Goals.Form.submitCreate)}
								cancelLabel={t(Resources.Goals.Form.cancel)}
								submitDisabled={submitDisabled}
								isSubmitting={isSubmitting}
								onCancel={goToList}
							/>
						)}
					</CollapseFadeTransition>
				</Stack>
			</form>
		</MyPlannLayout>
	);
};
